import { makeAutoObservable, runInAction } from 'mobx';
import { apiClient, envUrls } from '../api/api-client';
import { storage } from '../lib/storage';
import type { RootStore } from './root-store';
import { markPickHotel } from '../../features/hotel-picker/pick-flag';

// ===== Типы =====

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface JwtTokenPayload {
  exp: number;
  orig_iat: number;
  platform_id: number;
  user_id: number;
  username: string;
}

export interface AccountData {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  platform: number;
  platforms: any[];
  role?: string;
  [key: string]: any;
}

// ===== AuthStore =====

/**
 * AuthStore — полный цикл JWT авторизации.
 *
 * Воспроизводит логику из:
 * - modules/redux/auth/sagas.js (watchLoginRequest, getInitialData, loginFlow)
 * - modules/redux/account/sagas.js (watchChangePlatform)
 * - modules/redux/account/reducer.js (token storage, auth header)
 * - index.js (token expiration middleware)
 *
 * Ключи хранилища совместимы со старым кабинетом (@connectone/token).
 */
export class AuthStore {
  /** JWT токен */
  token: string | null = storage.local.get('token');

  /** Данные аккаунта (профиль + JWT payload) */
  account: AccountData | null = null;

  /** Состояние загрузки */
  isLoading = false;

  /** Первичная инициализация (не показываем UI пока не проверим токен) */
  isInitializing = true;

  /** Текст ошибки авторизации */
  error: string | null = null;

  /** Ошибки полей формы */
  fieldErrors: { username?: boolean; password?: boolean } = {};

  private root: RootStore;
  private tokenCheckInterval: ReturnType<typeof setInterval> | null = null;

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, {}, { autoBind: true });

    this.setupInterceptors();
    this.startTokenExpirationCheck();

    // Если токен есть — пробуем восстановить сессию
    if (this.token && !this.isTokenExpired) {
      this.restoreSession();
    } else if (this.token && this.isTokenExpired) {
      // Токен протух — разлогиниваем
      this.logout();
    } else {
      this.isInitializing = false;
    }
  }

  // ===== Computed =====

  get isAuthenticated(): boolean {
    return !!this.token && !!this.account;
  }

  get tokenPayload(): JwtTokenPayload | null {
    if (!this.token) return null;
    try {
      const payload = this.token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (_e) {
      return null;
    }
  }

  get isTokenExpired(): boolean {
    const data = this.tokenPayload;
    if (!data) return true;
    return data.exp * 1000 < Date.now();
  }

  get currentPlatformId(): number | null {
    return this.tokenPayload?.platform_id ?? this.account?.platform ?? null;
  }

  // ===== Actions =====

  /**
   * Логин — POST /auth/login
   * Воспроизводит watchLoginRequest из auth/sagas.js
   */
  async login(credentials: LoginCredentials) {
    // Обрезаем whitespace: пробелы / переносы / табы из копи-пасты —
    // самая частая причина "Ошибка сервера" при якобы правильных кредах.
    // .trim() в JS убирает все unicode-пробелы (включая   nbsp,
    // ​ zero-width space, ﻿ BOM) — это и нужно. Если у юзера
    // в пароле НАМЕРЕННО есть пробелы (что редкость), они могут быть
    // только в середине — они сохранятся.
    // .trim() в JS НЕ убирает BOM (U+FEFF) и zero-width characters
    // (U+200B..U+200D, U+2060) — а они часто прилетают из Word/PDF
    // через буфер обмена. Чистим явно через regex по краям.
    const INVISIBLE_EDGES =
      /^[\s ​‌‍⁠﻿]+|[\s ​‌‍⁠﻿]+$/g;
    const username = (credentials.username || '').replace(INVISIBLE_EDGES, '');
    const password = (credentials.password || '').replace(INVISIBLE_EDGES, '');

    // Валидация полей (как Joi schema в оригинале)
    const fieldErrors: { username?: boolean; password?: boolean } = {};
    if (!username) fieldErrors.username = true;
    if (!password) fieldErrors.password = true;

    if (Object.keys(fieldErrors).length > 0) {
      this.fieldErrors = fieldErrors;
      this.error = null;
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.fieldErrors = {};

    try {
      // 1. Логин
      const { data } = await apiClient.post(`${envUrls.api}/auth/login`, {
        username,
        password,
      });

      const { token, account } = data;

      // 2. Устанавливаем токен в axios
      apiClient.defaults.headers.common['Authorization'] = `JWT ${token}`;

      // 3. Загружаем платформы
      const platformsRes = await apiClient
        .get(`${envUrls.api}/api/v1/platforms`)
        // Аккаунт до одобрения заявки: объектов нет — пустой список, не ошибка входа.
        .catch(() => ({ data: { results: [] } }));

      // 4. Парсим JWT payload
      const parsedToken = this.parseJwt(token);
      const currentPlatformId = parsedToken?.platform_id;

      // 5. Собираем данные аккаунта (как в оригинале)
      const accountData: AccountData = {
        ...account,
        ...parsedToken,
        platform: currentPlatformId!,
        platforms: platformsRes.data?.results || [],
      };

      runInAction(() => {
        this.token = token;
        this.account = accountData;
        this.isLoading = false;
        this.isInitializing = false;

        storage.local.set('token', token);

        // Обновляем stores
        this.root.profile = accountData;
        this.root.platforms.setFromApi(accountData.platforms);
        // Сразу проставляем активную площадку (из JWT), иначе после
        // логина страницы, завязанные на activePlatformId (Настройки →
        // Отчёты и т.п.), пусты до ручного обновления браузера —
        // restoreSession это делает только при перезагрузке.
        if (currentPlatformId) {
          this.root.platforms.setActivePlatform(currentPlatformId);
        }
      });
      // Несколько отелей — после входа экран «Куда заходим?».
      // Суперадмину, поддержке и дилеру — нет: у них свои стартовые
      // разделы и десятки объектов.
      const acc = accountData as AccountData & { is_superadmin?: boolean; is_support?: boolean; dealer?: unknown };
      markPickHotel(
        !acc.is_superadmin && !acc.is_support && acc.dealer == null &&
        (accountData.platforms?.length || 0) >= 2,
      );
    } catch (err: any) {
      runInAction(() => {
        this.isLoading = false;

        // Без response → network/timeout/CORS, до сервера не дошли.
        if (!err?.response) {
          this.error = 'Не удалось связаться с сервером. Проверьте соединение.';
          return;
        }

        const status: number = err.response.status;
        const data = err.response.data;

        // 5xx — реально упавший сервер.
        if (status >= 500) {
          this.error = 'Ошибка сервера. Попробуйте позже.';
          return;
        }

        // Собираем человекочитаемое сообщение из всех известных форматов
        // ошибок (DRF / JWT / legacy). /auth/login возвращает 401 с
        // {"detail": "..."} либо {"non_field_errors": ["..."]}, разные
        // эндпоинты — по-разному.
        let backendMsg: string | null = null;
        const fieldErrs: { username?: boolean; password?: boolean } = {};
        if (data && typeof data === 'object') {
          if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
            backendMsg = String(data.non_field_errors[0]);
          } else if (typeof data.detail === 'string' && data.detail) {
            backendMsg = data.detail;
          } else if (typeof data.error === 'string' && data.error) {
            backendMsg = data.error;
          } else if (typeof data.message === 'string' && data.message) {
            backendMsg = data.message;
          }
          if (data.username) fieldErrs.username = true;
          if (data.password) fieldErrs.password = true;
        } else if (typeof data === 'string' && data) {
          backendMsg = data;
        }

        // 401/403 — почти всегда «неверный логин/пароль». Перекрываем
        // криптические английские сообщения JWT-токенайзера понятным
        // русским, если в backendMsg нет кириллицы.
        if (status === 401 || status === 403) {
          this.fieldErrors = { username: true, password: true };
          const hasCyrillic = !!backendMsg && /[А-Яа-яЁё]/.test(backendMsg);
          this.error = hasCyrillic ? backendMsg : 'Неверный логин или пароль';
          return;
        }

        // 400 — валидация (например, пустые поля).
        if (status === 400) {
          if (Object.keys(fieldErrs).length > 0) {
            this.fieldErrors = fieldErrs;
          }
          this.error = backendMsg || 'Проверьте введённые данные';
          return;
        }

        // Прочие 4xx
        this.error = backendMsg || 'Не удалось войти. Попробуйте ещё раз.';
      });
    }
  }

  /**
   * Восстановление сессии при наличии токена.
   * Воспроизводит getInitialData из auth/sagas.js
   */
  async restoreSession() {
    try {
      const token = this.token!;
      apiClient.defaults.headers.common['Authorization'] = `JWT ${token}`;

      const parsedToken = this.parseJwt(token);
      const currentPlatformId = parsedToken?.platform_id;

      // Параллельно грузим профиль и платформы
      const [profileRes, platformsRes] = await Promise.all([
        apiClient.get(`${envUrls.api}/auth/profile`),
        apiClient
        .get(`${envUrls.api}/api/v1/platforms`)
        // Аккаунт до одобрения заявки: объектов нет — пустой список, не ошибка входа.
        .catch(() => ({ data: { results: [] } })),
      ]);

      const accountData: AccountData = {
        ...parsedToken,
        ...profileRes.data,
        platform: currentPlatformId!,
        platforms: platformsRes.data?.results || [],
      };

      runInAction(() => {
        this.account = accountData;
        this.isInitializing = false;

        this.root.profile = accountData;
        this.root.platforms.setFromApi(accountData.platforms);
        if (currentPlatformId) {
          this.root.platforms.setActivePlatform(currentPlatformId);
        }
      });
    } catch (err) {
      console.error('Failed to restore session:', err);
      runInAction(() => {
        this.isInitializing = false;
      });
      this.logout();
    }
  }

  /**
   * Смена платформы — POST /auth/refresh
   * Воспроизводит watchChangePlatform из account/sagas.js
   */
  async changePlatform(nextPlatformId: number) {
    if (!this.token) return;

    try {
      // 1. Refresh token с новой платформой
      const { data } = await apiClient.post(`${envUrls.api}/auth/refresh`, {
        token: this.token,
        platform_id: nextPlatformId,
      });

      const { token: newToken, account } = data;
      apiClient.defaults.headers.common['Authorization'] = `JWT ${newToken}`;

      // 2. Загружаем платформы
      const platformsRes = await apiClient
        .get(`${envUrls.api}/api/v1/platforms`)
        // Аккаунт до одобрения заявки: объектов нет — пустой список, не ошибка входа.
        .catch(() => ({ data: { results: [] } }));

      const accountData: AccountData = {
        ...account,
        platform: nextPlatformId,
        platforms: platformsRes.data?.results || [],
      };

      runInAction(() => {
        this.token = newToken;
        this.account = accountData;
        storage.local.set('token', newToken);

        this.root.profile = accountData;
        this.root.platforms.setFromApi(accountData.platforms);
        this.root.platforms.setActivePlatform(nextPlatformId);
      });

      return true;
    } catch (err) {
      console.error('Failed to change platform:', err);
      return false;
    }
  }

  /**
   * Выход
   */
  logout() {
    markPickHotel(false);
    this.token = null;
    this.account = null;
    this.error = null;
    this.fieldErrors = {};
    this.isInitializing = false;

    this.root.profile = null;
    // logout() зовётся и из конструктора (протухший токен в localStorage),
    // когда RootStore ещё не создал onboarding — иначе белый экран.
    this.root.onboarding?.reset();

    storage.local.remove('token');
    storage.local.remove('firebase');

    // Чистим axios header
    delete apiClient.defaults.headers.common['Authorization'];
  }

  // ===== Private =====

  private parseJwt(token: string): JwtTokenPayload | null {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (_e) {
      return null;
    }
  }

  /**
   * Периодическая проверка token expiration.
   * Воспроизводит логику из index.js middleware.
   */
  private startTokenExpirationCheck() {
    this.tokenCheckInterval = setInterval(() => {
      if (this.token && this.isTokenExpired) {
        console.warn('Token expired, logging out');
        this.logout();
      }
    }, 60_000); // Проверка каждую минуту
  }

  /**
   * Настройка axios interceptors
   */
  private setupInterceptors() {
    // Request: добавляем токен
    apiClient.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `JWT ${this.token}`;
      }
      return config;
    });

    // Response: обработка 401 (НЕ 403).
    //
    // Раньше тут было `401 || 403` — и это была главная причина
    // «белого экрана» у отелей (#402). 403 не означает «токен
    // протух»: это «текущий пользователь не имеет доступа к этому
    // эндпоинту». Например, fitness_staff не имеет permission на
    // /api/v1/platforms (#255) — один такой запрос разлогинивал
    // его полностью и стирал токен. После logout рендер
    // полузагруженной страницы падал на `account=null` и оставался
    // белым.
    //
    // Теперь: logout только на 401 (Unauthorized — токен невалиден
    // или протух). На 403 отдельные компоненты сами должны
    // обработать (показать «нет доступа» в своём UI).
    apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
        }
        return Promise.reject(error);
      },
    );
  }

  /**
   * Очистка (для hot-reload и тестов)
   */
  dispose() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  }
}
